import { getVariantDisplayQuantity } from '../../utils/variant-display';
import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';
export interface StoreProduct {
  store_id: number | null;
  variant_id: number;
  price: number;
  stock: number;
  is_available: boolean;
  low_stock_threshold?: number;
}

export interface StoreProductStockUpdateResult {
  id: number;
  store_id?: number;
  variant_id?: number;
  stock: number;
  is_available: boolean;
  low_stock_threshold?: number;
  price?: number;
  status?: string;
  updated_at?: string;
}

export interface StoreProductUpdateResult {
  id: number;
  price: number;
  stock: number;
  is_available: boolean;
  updated_at?: string;
}

export interface AssignmentView {
  store_product_id?: number;
  variant_id: number;
  variant_name: string;
  mrp: number;
  price: number;
  stock: number;
  low_stock_threshold: number;
  is_available: boolean;
  isAssigned: boolean;
}

export interface StoreProductsPage {
  data: unknown[];
  count: number;
}
@Injectable({ providedIn: 'root' })
export class StoreProductService {
  private table = 'store_products';
  private pendingMutationRequests = new Map<string, string>();

  getAssignments(
    storeId: number,
    productId: number
  ): Observable<AssignmentView[]> {
    return from(
      (async () => {
        // 1. Fetch all variants for the product
        const { data: variants, error: vError } = await supabase
          .from('product_variants')
          .select('id, sku, quantity, unit, net_quantity, mrp, is_active')
          .eq('is_active', true)
          .eq('product_id', productId);

        if (vError) throw vError;

        // 2. Fetch existing assignments for this store
        const { data: assignments, error: aError } = await supabase
          .from(this.table)
          .select('*')
          .eq('store_id', storeId);

        if (aError) throw aError;

        // 3. Create lookup map (faster than .find for every variant)
        const assignmentMap = new Map(
          (assignments ?? []).map((a) => [a.variant_id, a])
        );

        // 4. Merge variants with assignments
        return (variants ?? []).map((v) => {
          const assignment = assignmentMap.get(v.id);

          return {
            store_product_id: assignment?.id,
            variant_id: v.id,
            variant_name: `${getVariantDisplayQuantity(v)} (${v.sku})`,
            mrp: v.mrp,

            // Existing assignment price OR default 10% discount
            price: assignment?.price ?? Number((v.mrp * 0.9).toFixed(2)),

            stock: assignment?.stock ?? 0,
            low_stock_threshold: assignment?.low_stock_threshold ?? 5,
            is_available: assignment?.is_available ?? true,

            // Whether assignment exists
            isAssigned: !!assignment,
          };
        });
      })()
    );
  }

  getStoreProducts(filters?: {
    storeId?: number;
    search?: string;
    availability?: 'all' | 'available' | 'low' | 'out';
    page?: number;
    pageSize?: number;
  }): Observable<StoreProductsPage> {
    const page = filters?.page && filters.page > 0 ? filters.page : undefined;
    const pageSize =
      filters?.pageSize && filters.pageSize > 0 ? filters.pageSize : undefined;

    // Inventory filters run on the full loaded set, not the API's default first page.
    if (!page || !pageSize) {
      return from((async () => {
        const rows: unknown[] = [];
        let count = 0;
        for (let nextPage = 1; ; nextPage++) {
          const result = await firstValueFrom(this.getStoreProducts({ ...filters, page: nextPage, pageSize: 500 }));
          rows.push(...result.data);
          count = result.count;
          if (result.data.length < 500 || rows.length >= count) break;
        }
        return { data: rows, count };
      })());
    }

    let query = supabase
      .from('store_products')
      .select(
        `
      id,
      store_id,
      price,
      stock,
      is_available,
      low_stock_threshold,
      created_at,
      updated_at,

      store:stores (id, name),

      variant:product_variants (
        id,
        net_quantity,
        sku,
        quantity,
        unit,
        mrp,

        product:product_id (
          id,
          name,
       categories:product_categories (
    categories (
      id,
      name
    )
  ),
          brand:brands (id, name),
          images:product_images  (
        is_primary,
    files!product_images_file_id_fkey (
      bucket,
      path
    )
          )
        )
      )
    `,
        { count: 'exact' }
      )
      .order('created_at', { ascending: false })
      .order('id', { ascending: false });

    if (page && pageSize) {
      const rangeFrom = (page - 1) * pageSize;
      const rangeTo = rangeFrom + pageSize - 1;
      query = query.range(rangeFrom, rangeTo);
    }

    if (filters?.storeId) {
      query = query.eq('store_id', filters.storeId);
    }

    if (filters?.availability === 'available') {
      query = query.eq('is_available', true).gt('stock', 0);
    }

    if (filters?.availability === 'out') {
      query = query.eq('stock', 0);
    }

    // search by product name
    if (filters?.search) {
      query = query.ilike('variant.product.name', `%${filters.search}%`);
    }

    return from(
      query.then(({ data, error, count }) => {
        if (error) throw error;
        return {
          data: data ?? [],
          count: count ?? 0,
        };
      })
    );
  }

  getStoreProductGroups(filters?: {
    storeId?: number; search?: string; availability?: string; categoryIds?: number[];
    page?: number; pageSize?: number; sort?: string; ascending?: boolean;
  }): Observable<StoreProductsPage> {
    return from(supabase.rpc(filters?.categoryIds?.length ? 'admin_store_product_groups_filtered' : 'admin_store_product_groups', {
      ...(filters?.categoryIds?.length ? { p_category_ids: filters.categoryIds } : {}),
      p_store_id: filters?.storeId ?? null, p_search: filters?.search ?? '',
      p_status: filters?.availability ?? 'all', p_page: filters?.page ?? 1,
      p_page_size: filters?.pageSize ?? 10, p_sort: filters?.sort ?? 'name',
      p_ascending: filters?.ascending ?? true,
    }).then(async ({ data, error }) => {
      if (error) throw error;
      const groups = data?.data ?? [];
      const ids = [...new Set<number>(groups
        .map((item: any) => Number(item.variant?.id)).filter((id: number) => id > 0))];
      if (ids.length) {
        const { data: variants, error: variantsError } = await supabase.from('product_variants')
          .select('id, net_quantity').in('id', ids);
        if (variantsError) throw variantsError;
        const byId = new Map((variants || []).map(variant => [Number(variant.id), variant.net_quantity]));
        for (const item of groups) {
          if (item.variant) item.variant.net_quantity = byId.get(Number(item.variant.id)) ?? item.variant.net_quantity;
        }
      }
      return { data: groups, count: data?.count ?? 0 };
    }));
  }

  mutateStoreProduct(params: {
    reason?: string;
    storeProductId: number;
    setStock?: number;
    addStock?: number;
    isAvailable?: boolean;
    lowStockThreshold?: number;
    price?: number;
  }): Observable<StoreProductStockUpdateResult> {
    const fingerprint = JSON.stringify(params);
    const requestId = this.pendingMutationRequests.get(fingerprint) ?? crypto.randomUUID();
    this.pendingMutationRequests.set(fingerprint, requestId);
    return from(
      supabase
        .rpc('admin_mutate_store_product_v2', {
          p_request_id: requestId,
          p_store_product_id: params.storeProductId,
          p_patch: {
            reason: params.reason?.trim() || null,
            set_stock: params.setStock ?? null,
            add_stock: params.addStock ?? null,
            is_available: params.isAvailable ?? null,
            low_stock_threshold: params.lowStockThreshold ?? null,
            price: params.price ?? null,
          },
        })
        .then(({ data, error }) => {
          if (error) throw error;
          this.pendingMutationRequests.delete(fingerprint);
          return data as StoreProductStockUpdateResult;
        })
    );
  }

  updateStoreProductStockById(
    storeProductId: number,
    stock: number
  ): Observable<StoreProductStockUpdateResult> {
    return this.mutateStoreProduct({
      storeProductId,
      setStock: stock,
    });
  }

  async bulkUpsertAssignments(payload: Pick<StoreProduct, 'store_id' | 'variant_id' | 'price' | 'is_available' | 'low_stock_threshold'>[]) {
    const { error } = await supabase.rpc(
      'admin_save_store_product_assignments',
      {
        p_items: payload.map((row) => ({
          store_id: row.store_id,
          variant_id: row.variant_id,
          price: row.price,
          is_available: row.is_available ?? true,
          low_stock_threshold: row.low_stock_threshold ?? 5,
        })),
      }
    );

    if (error) throw error;
  }

  updateStoreProductById(
    storeProductId: number,
    patch: { price?: number; stock?: number; is_available?: boolean; max_order_quantity?: number | null }
  ): Observable<StoreProductUpdateResult> {
    if ('max_order_quantity' in patch) {
      return from(supabase.rpc('admin_update_store_product_settings', {
        p_store_product_id: storeProductId,
        p_price: patch.price,
        p_is_available: patch.is_available,
        p_max_order_quantity: patch.max_order_quantity,
      }).then(({ data, error }) => {
        if (error) throw error;
        return data as StoreProductUpdateResult;
      }));
    }
    return this.mutateStoreProduct({
      storeProductId,
      setStock: patch.stock,
      isAvailable: patch.is_available,
      price: patch.price,
    }) as Observable<StoreProductUpdateResult>;
  }

  deleteStoreProductById(storeProductId: number, storeId: number): Observable<void> {
    return from(
      supabase
        .from(this.table)
        .delete()
        .eq('id', storeProductId)
        .eq('store_id', storeId)
        .select('id')
        .then(({ data, error }) => {
          if (error) {
            if (error.code === '23503') {
              throw new Error('This variant is linked to inventory history. Turn off Available in store in Edit settings instead.');
            }
            if (error.code === '42501') throw new Error('Only a superadmin can remove store mappings.');
            if (error.message?.includes('Set stock to zero')) {
              throw new Error('Stock remains for this variant. Use Update stock with a reason to reconcile it to zero, or turn off Available in store.');
            }
            if (error.message?.includes('INVENTORY_MAPPING_HAS_ORDER_HISTORY')) {
              throw new Error('This variant has order history. Turn off Available in store in Edit settings to preserve returns and refunds.');
            }
            throw new Error('Could not remove this store mapping. Refresh inventory and try again.');
          }
          if (data?.length !== 1) {
            throw new Error('No mapping was removed. It may have changed, or your account lacks removal permission. Refresh inventory; removal requires a superadmin.');
          }
        })
    );
  }

}
